import { Field, FieldRenderProps } from 'react-final-form';
import isEqual from 'lodash/isEqual';

import { Autocomplete, renderAutocompleteInput } from '@occc/ui-design-system';
import {
  AutocompleteOption,
  AutoCompleteValue,
  showErrorOnBlurOrDirty,
} from '@occc-app/design-system';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

export type SelectAutocompleteProps<FieldName extends string> = {
  name: FieldName;
  label: string;
  placeholder?: string;
  options: AutocompleteOption[];
  disabled?: boolean;
};

// TODO: Make it a design system component?
// Generics do not play well with arrow functions for components (can't understand if that's JSX),
// so we use a regular function here.
export function SelectAutocomplete<FieldName extends string>(
  props: SelectAutocompleteProps<FieldName>,
) {
  const { name, label, placeholder, options, disabled } = props;
  const { isEditable } = useCanEditFormFieldsContext();
  const isDisabled = disabled || !isEditable;

  const renderInput = ({ input, meta }: FieldRenderProps<unknown>) => {
    const isError = showErrorOnBlurOrDirty({ meta });
    return (
      <Autocomplete
        // I can't get types correct at this moment, because TS does not know about parse-format
        value={(input.value as AutocompleteOption) || []}
        onChange={(_, option) => {
          input.onChange(option);
        }}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        blurOnSelect
        isOptionEqualToValue={(option: AutocompleteOption, currentValue: AutocompleteOption) =>
          option.value === currentValue.value
        }
        options={options}
        multiple
        disabled={isDisabled}
        renderInput={(params) => {
          return renderAutocompleteInput(params, {
            errorText: isError ? meta.error : '',
            label,
            placeholder,
            inputProps: {
              margin: 'none',
            },
          });
        }}
      />
    );
  };

  return (
    <Field
      render={renderInput}
      isEqual={isEqual}
      name={name}
      parse={(option: AutoCompleteValue): string[] => {
        if (Array.isArray(option)) {
          return option.map((o) => o.value);
        }
        return option ? [option.value] : [];
      }}
      format={(value) =>
        (value || []).map((selectedValue) => {
          const option = options.find((o) => o.value === selectedValue);
          return { value: selectedValue, label: option?.label ?? selectedValue };
        })
      }
    />
  );
}
